<template>
  <Dialog ref="dialog">
    <template #expander="{ openModal }">
      <button
        class="rpt-btn rpt-filter-btn"
        @click="() => { restoreSelection(); openModal(); }"
      >
        <svg class="rpt-filter-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
        <span v-if="selectedCount">
          {{ selectedCount }} Player{{ selectedCount > 1 ? 's' : '' }}
        </span>
        <span v-else>All Players</span>
      </button>
    </template>
    <template #dialog.content>
      <SimpleEntityListingPage
        entityItemsNamePlural="Players"
        entityItemName="Player"
        areaAccessName="Players"
        :ItemsLister="PlayersLister"
        :selectMode="true"
      />
      <div class="rpt-dialog-actions">
        <ButtonCustomized
          label="Cancel"
          :secondary="true"
          :disabled="isApplying"
          @click="onCancel"
        />
        <ButtonCustomized
          label="Clear Filter"
          :secondary="true"
          :disabled="isApplying"
          @click="onClear"
        />
        <ButtonCustomized
          label="Apply Filter"
          :loading="isApplying"
          @click="onApply"
        />
      </div>
    </template>
  </Dialog>
</template>

<script lang="ts" setup>
import { ref, computed } from 'vue';
import Dialog from '@/components/Dialog/Dialog.vue';
import ButtonCustomized from '@/components/form-fields/ButtonCustomized.vue';
import SimpleEntityListingPage from '@/components/ListingPages/SimpleEntityListingPage/SimpleEntityListingPage.vue';
import PlayersLister from '@/views/PlayersListView/PlayersLister/PlayersLister.vue';
import useListSelection from '@/components/ListingPages/ItemsLister/ItemsList/composables/listSelection';
import { getPlayerById } from '@/helpers/api/player/player';

interface SelectableEntity {
  id: number | string;
  [key: string]: unknown;
}

const emit = defineEmits<{
  // Empty array == clear filter.
  (e: 'apply', deviceUuids: string[]): void;
}>();

const dialog = ref<InstanceType<typeof Dialog> | null>(null);
const isApplying = ref(false);
// Last-applied selection, restored when the dialog reopens.
const savedItems = ref<SelectableEntity[]>([]);

const selectedCount = computed(() => savedItems.value.length);

// useListSelection is a singleton across the app — see CODE_REVIEW.md §1.3.
const {
  selectedItems: dialogSelectedItems,
  clearItemsSelection: clearDialogSelection,
  addMultipleItemsToSelection: addMultipleToDialogSelection,
} = useListSelection();

// KNOWN WORKAROUND — see the long-form explanation in MediaFilterDialog.vue
// (search for "KNOWN WORKAROUND") and CODE_REVIEW.md §1.3. tl;dr:
// useListSelection's global document.body click listener wipes the selection
// as the opener click bubbles, so we defer restore to a macrotask. To remove
// this: add a `{ scoped: true }` option to useListSelection that skips the
// global listener, then switch both filter dialogs to it.
function restoreSelection() {
  setTimeout(() => {
    clearDialogSelection();
    if (savedItems.value.length) {
      addMultipleToDialogSelection(savedItems.value, {
        itemIdGetterCallback: (item: SelectableEntity) => item.id,
      });
    }
  }, 0);
}

async function onApply() {
  if (isApplying.value) return; // double-click guard
  isApplying.value = true;
  try {
    if (dialogSelectedItems.length === 0) {
      savedItems.value = [];
      emit('apply', []);
    } else {
      const items = [...dialogSelectedItems] as SelectableEntity[];
      savedItems.value = items;
      // The lister gives us player records by id; the metrics endpoint
      // wants device UUIDs, so we have to round-trip through getPlayerById.
      const responses = await Promise.all(
        items.map((p) => getPlayerById(String(p.id))),
      );
      const uuids = responses
        .map((r: any) => (Array.isArray(r) ? r[0] : r))
        .filter((d: any) => d?.deviceUuid)
        .map((d: any) => d.deviceUuid as string);
      emit('apply', uuids);
    }
    dialog.value?.closeModal();
  } finally {
    isApplying.value = false;
  }
}

function onCancel() {
  dialog.value?.closeModal();
}

function onClear() {
  savedItems.value = [];
  clearDialogSelection();
  emit('apply', []);
  dialog.value?.closeModal();
}

defineExpose({ open: () => dialog.value?.openModal() });
</script>

<style scoped>
@import '@/views/ReportsView/styles/reports-shared.css';

.rpt-filter-btn {
  gap: 0.4rem;
  font-weight: 500;
}

.rpt-filter-btn .rpt-filter-icon {
  width: 15px;
  height: 15px;
  flex-shrink: 0;
}

.rpt-dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  padding: 1rem 1.25rem;
  border-top: 1px solid #e5e7eb;
}
</style>
